/**
 * Wave 11: Additional Global Cities
 * Wuhan, Hangzhou, Nanjing, Kuala Lumpur II -> Penang, Lahore, Riyadh II -> Jeddah,
 * Johannesburg II -> Durban, Auckland, Vancouver, Chicago
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling. React, Node.js, Python, cloud-native.' },
  { name: 'Web Application Development', description: 'Enterprise web applications, portals, dashboards, and internal tools. Modern stack with React, TypeScript, and cloud infrastructure.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps with React Native and Flutter. From MVP to enterprise-grade mobile solutions.' },
  { name: 'AI & Machine Learning', description: 'AI-powered solutions: NLP, computer vision, predictive analytics, recommendation engines, and LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP infrastructure. CI/CD pipelines, containerization, monitoring, and 24/7 managed services.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA engineers, DevOps specialists, and project managers integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, and design systems. Figma-first approach with accessibility compliance.' },
  { name: 'Cybersecurity', description: 'Penetration testing, security audits, SOC operations, and compliance (ISO 27001, GDPR, HIPAA, PCI-DSS).' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools, and banking applications with PCI-DSS and SOC 2 compliance.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, inventory management, and omnichannel retail solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine platforms, EHR integrations, and HIPAA-compliant health applications.' },
  { name: 'SaaS & Technology', description: 'Product development, scaling, and modernization for SaaS companies from seed to Series C+.' },
  { name: 'Real Estate & PropTech', description: 'Property management platforms, virtual tours, tenant portals, and real estate marketplaces.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse management, and supply chain visibility platforms.' },
];

export const CITY_AGENCY_PAGES_WAVE11: Record<string, CityAgencyData> = {

  wuhan: {
    slug: 'software-development-agency-wuhan',
    cityName: 'Wuhan',
    countryName: 'China',
    flag: '🇨🇳',
    timezone: 'CST (GMT+8)',
    currency: 'CNY/USD',
    title: {
      en: 'Software Development Agency Wuhan | Call IT Dev',
      fr: 'Agence de Développement Informatique Wuhan | Call IT Dev',
      zh: '武汉软件开发公司 | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Wuhan, China\'s central tech hub. SaaS, AI, cloud. 200+ engineers, competitive rates.',
      fr: 'Agence de développement informatique pour Wuhan. SaaS, IA, cloud. 200+ ingénieurs.',
      zh: '为武汉——中国中部科技中心提供软件开发服务。SaaS、AI、云。200+工程师。',
    },
    keywords: ['software development agency Wuhan', '武汉软件开发公司', 'IT outsourcing Wuhan', 'AI development Wuhan'],
    heroTitle: { en: 'Software Development Agency for Wuhan', zh: '武汉软件开发合作伙伴' },
    heroSubtitle: {
      en: 'Central China\'s tech powerhouse. Enterprise software, AI, and optics industry tech at competitive rates.',
      zh: '中国中部科技引擎。企业软件、AI和光电产业技术，价格极具竞争力。',
    },
    whyTitle: { en: 'Why Choose Our Agency for Wuhan Projects', zh: '为什么选择我们的武汉项目团队' },
    whyReasons: [
      { icon: '🔬', title: 'Optics & Photonics Tech', description: 'Software for Wuhan\'s world-leading optics and laser industry — Optics Valley of China.' },
      { icon: '🎓', title: 'University Talent Pool', description: 'Home to 80+ universities including WHU and HUST. Deep engineering talent.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Premium talent at significant savings vs coastal Chinese cities.' },
      { icon: '🏗️', title: 'Smart City Solutions', description: 'IoT, urban computing, and smart infrastructure for China\'s fastest-growing metropolis.' },
      { icon: '🤖', title: 'AI Research Hub', description: 'Major AI labs and research institutes. Cutting-edge ML capabilities.' },
      { icon: '🌐', title: 'Bilingual Teams', description: 'Mandarin and English-speaking engineers for international projects.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Wuhan for software development?', answer: 'Wuhan is China\'s Optics Valley with 80+ universities, a massive engineering talent pool, and 30-40% lower costs than Shanghai/Beijing.' },
      { question: 'Do you build optics/photonics software?', answer: 'Yes. Laser control systems, optical simulation, and smart manufacturing for Wuhan\'s photonics industry.' },
      { question: 'What about data compliance?', answer: 'Full PIPL and CSL compliance. Enterprise security standards in development practices.' },
      { question: 'How fast can you start?', answer: 'Team ready in 2 weeks. First sprint week 3. Scale from 2 to 30+ engineers.' },
      { question: 'Do you support smart city projects?', answer: 'Yes — IoT platforms, urban analytics, traffic management, and city-scale infrastructure monitoring.' },
      { question: 'Cost comparison to Wuhan agencies?', answer: 'Senior developers in Wuhan cost ¥500-1000/hr. Our rate is ¥250-500/hr — 50% savings.' },
    ],
    localRate: '¥500-1000',
    ourRate: '¥250-500',
    savings: '50%+',
  },

  hangzhou: {
    slug: 'software-development-agency-hangzhou',
    cityName: 'Hangzhou',
    countryName: 'China',
    flag: '🇨🇳',
    timezone: 'CST (GMT+8)',
    currency: 'CNY/USD',
    title: {
      en: 'Software Development Agency Hangzhou | Call IT Dev',
      fr: 'Agence de Développement Informatique Hangzhou | Call IT Dev',
      zh: '杭州软件开发公司 | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Hangzhou, China\'s e-commerce capital. Alibaba ecosystem, fintech, AI. 200+ engineers.',
      fr: 'Agence de développement informatique pour Hangzhou. Écosystème Alibaba, fintech, IA. 200+ ingénieurs.',
      zh: '为杭州——中国电商之都提供软件开发服务。阿里巴巴生态、金融科技、AI。200+工程师。',
    },
    keywords: ['software development agency Hangzhou', '杭州软件开发公司', 'e-commerce development Hangzhou', 'Alibaba ecosystem development'],
    heroTitle: { en: 'Software Development Agency for Hangzhou', zh: '杭州软件开发合作伙伴' },
    heroSubtitle: {
      en: 'Home of Alibaba and China\'s e-commerce revolution. E-commerce, fintech, and AI development at competitive rates.',
      zh: '阿里巴巴故乡和中国电商革命中心。电商、金融科技和AI开发，价格极具竞争力。',
    },
    whyTitle: { en: 'Why Choose Our Agency for Hangzhou Projects', zh: '为什么选择我们的杭州项目团队' },
    whyReasons: [
      { icon: '🛒', title: 'E-commerce Capital', description: 'Build for the Alibaba ecosystem: Taobao, Tmall, 1688, AliExpress integrations.' },
      { icon: '💳', title: 'FinTech Expertise', description: 'Alipay, Ant Group ecosystem. Payment, lending, and insurance tech platforms.' },
      { icon: '💰', title: '50% Cost Savings', description: 'Premium engineering at 50% less than local Hangzhou agency rates.' },
      { icon: '🤖', title: 'AI City', description: 'China\'s AI City Brain initiative started here. Smart city and AI expertise.' },
      { icon: '🌊', title: 'Digital Lifestyle', description: 'Live commerce, social commerce, and new retail technology.' },
      { icon: '📱', title: 'Super App Development', description: 'Mini programs, super app integrations, and platform ecosystems.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you build for the Alibaba ecosystem?', answer: 'Yes. Taobao/Tmall store integrations, 1688 B2B platforms, AliExpress cross-border, and Alibaba Cloud solutions.' },
      { question: 'Can you integrate with Alipay?', answer: 'Absolutely. Alipay payments, mini programs, and Ant Group financial service APIs.' },
      { question: 'What about live commerce platforms?', answer: 'We build live streaming commerce, social selling, and new retail platforms — core to Hangzhou\'s digital economy.' },
      { question: 'Cost comparison?', answer: 'Hangzhou senior dev: ¥700-1400/hr. Our rate: ¥350-650/hr. 50% savings.' },
      { question: 'Do you handle cross-border e-commerce?', answer: 'Yes. Platforms for Chinese sellers on global markets and international brands entering China via Tmall Global.' },
      { question: 'Minimum team size?', answer: 'Start with 2 developers. Scale to 40+. Flexible monthly contracts.' },
    ],
    localRate: '¥700-1400',
    ourRate: '¥350-650',
    savings: '50%+',
  },

  nanjing: {
    slug: 'software-development-agency-nanjing',
    cityName: 'Nanjing',
    countryName: 'China',
    flag: '🇨🇳',
    timezone: 'CST (GMT+8)',
    currency: 'CNY/USD',
    title: {
      en: 'Software Development Agency Nanjing | Call IT Dev',
      zh: '南京软件开发公司 | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Nanjing, China. Enterprise software, telecom, AI. 200+ engineers, competitive rates.',
      zh: '为南京提供软件开发服务。企业软件、电信、AI。200+工程师，价格具有竞争力。',
    },
    keywords: ['software development agency Nanjing', '南京软件开发公司', 'telecom software Nanjing', 'enterprise development Nanjing'],
    heroTitle: { en: 'Software Development Agency for Nanjing', zh: '南京软件开发合作伙伴' },
    heroSubtitle: {
      en: 'Jiangsu\'s tech capital. Enterprise software, telecom, and AI solutions at competitive global rates.',
      zh: '江苏科技之都。企业软件、电信和AI解决方案，全球竞争力价格。',
    },
    whyTitle: { en: 'Why Choose Our Agency for Nanjing', zh: '为什么选择我们的南京项目团队' },
    whyReasons: [
      { icon: '📡', title: 'Telecom Expertise', description: 'Software for China\'s telecom giants. 5G, network management, and OSS/BSS systems.' },
      { icon: '🎓', title: 'Elite Universities', description: 'Nanjing University, Southeast University — top CS programs in China.' },
      { icon: '💰', title: '50% Cost Savings', description: 'Lower costs than Shanghai with comparable talent quality.' },
      { icon: '🏛️', title: 'Government Tech', description: 'E-government, smart city, and public sector digitization projects.' },
      { icon: '🔬', title: 'Research Software', description: 'Scientific computing, simulation, and research management platforms.' },
      { icon: '🌐', title: 'Yangtze Delta Hub', description: 'Strategic location serving the entire Yangtze River Delta economic zone.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you build telecom software?', answer: 'Yes. OSS/BSS, network management, 5G applications, and telecom billing platforms.' },
      { question: 'Why Nanjing over Shanghai?', answer: 'Nanjing offers 30% lower costs with elite university talent from NJU and SEU. Excellent for R&D-heavy projects.' },
      { question: 'Government projects?', answer: 'E-government platforms, public data analytics, and smart city infrastructure.' },
      { question: 'Cost comparison?', answer: 'Nanjing senior dev: ¥600-1100/hr. Our rate: ¥300-550/hr. 50% savings.' },
      { question: 'Data compliance?', answer: 'Full PIPL, CSL compliance. Enterprise security standards.' },
      { question: 'Minimum engagement?', answer: 'Start with 2 engineers. Scale as needed. No long-term lock-in.' },
    ],
    localRate: '¥600-1100',
    ourRate: '¥300-550',
    savings: '50%+',
  },

  penang: {
    slug: 'software-development-agency-penang',
    cityName: 'Penang',
    countryName: 'Malaysia',
    flag: '🇲🇾',
    timezone: 'MYT (GMT+8)',
    currency: 'MYR/USD',
    title: {
      en: 'Software Development Agency Penang | Call IT Dev',
      fr: 'Agence de Développement Informatique Penang | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Penang, Malaysia\'s Silicon Island. Electronics, IoT, semiconductor software. 200+ engineers.',
      fr: 'Agence de développement informatique pour Penang. Électronique, IoT, semi-conducteurs. 200+ ingénieurs.',
    },
    keywords: ['software development agency Penang', 'IT outsourcing Penang', 'semiconductor software Penang', 'IoT development Malaysia'],
    heroTitle: { en: 'Software Development Agency for Penang' },
    heroSubtitle: { en: 'Malaysia\'s Silicon Island. Semiconductor, IoT, and electronics software at competitive rates.' },
    whyTitle: { en: 'Why Penang Companies Choose Call IT Dev' },
    whyReasons: [
      { icon: '💎', title: 'Silicon Island', description: 'Software for Penang\'s world-class semiconductor and electronics industry.' },
      { icon: '🔧', title: 'IoT & Embedded', description: 'Firmware, embedded systems, and IoT platforms for electronics manufacturers.' },
      { icon: '💰', title: '50% Cost Savings', description: 'Premium engineering at half the cost of local Penang tech agencies.' },
      { icon: '🌐', title: 'Multilingual Teams', description: 'English, Malay, and Mandarin-speaking engineers.' },
      { icon: '🏭', title: 'Manufacturing Tech', description: 'MES, quality control, and supply chain software for Penang factories.' },
      { icon: '📊', title: 'Data Analytics', description: 'Manufacturing analytics, yield optimization, and predictive maintenance.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you build semiconductor software?', answer: 'Yes. EDA support tools, yield management, test automation, and supply chain platforms for chip manufacturers.' },
      { question: 'IoT for electronics?', answer: 'Firmware, edge computing, IoT platforms, and cloud backends for connected electronic products.' },
      { question: 'Cost comparison?', answer: 'Penang senior dev: RM250-500/hr. Our rate: RM125-250/hr. 50% savings.' },
      { question: 'Do you support Malay and Mandarin?', answer: 'Yes. Our teams communicate in English, Malay, and Mandarin.' },
      { question: 'Manufacturing integration?', answer: 'MES, SCADA, ERP integrations for Penang\'s manufacturing ecosystem.' },
      { question: 'Minimum engagement?', answer: 'Start with 2 engineers. Scale to 20+. Flexible contracts.' },
    ],
    localRate: 'RM250-500',
    ourRate: 'RM125-250',
    savings: '50%+',
  },

  lahore: {
    slug: 'software-development-agency-lahore',
    cityName: 'Lahore',
    countryName: 'Pakistan',
    flag: '🇵🇰',
    timezone: 'PKT (GMT+5)',
    currency: 'PKR/USD',
    title: {
      en: 'Software Development Agency Lahore | Call IT Dev',
      fr: 'Agence de Développement Informatique Lahore | Call IT Dev',
      ar: 'وكالة تطوير برمجيات لاهور | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Lahore, Pakistan\'s tech hub. SaaS, mobile, AI. 200+ engineers, competitive rates.',
      fr: 'Agence de développement informatique pour Lahore. SaaS, mobile, IA. 200+ ingénieurs.',
      ar: 'وكالة تطوير برمجيات لاهور، باكستان. SaaS، موبايل، ذكاء اصطناعي.',
    },
    keywords: ['software development agency Lahore', 'IT outsourcing Lahore', 'app development Pakistan', 'SaaS development Lahore'],
    heroTitle: { en: 'Software Development Agency for Lahore' },
    heroSubtitle: { en: 'Pakistan\'s tech capital. SaaS, mobile, and AI development with world-class engineering at competitive rates.' },
    whyTitle: { en: 'Why Choose Our Agency for Lahore Projects' },
    whyReasons: [
      { icon: '🎓', title: 'LUMS & FAST Talent', description: 'Access to Pakistan\'s top CS graduates from LUMS, FAST, and NUST.' },
      { icon: '💰', title: '65% Cost Savings', description: 'Premium engineering at a fraction of Western rates. Lahore\'s cost advantage is massive.' },
      { icon: '🌐', title: 'English-Speaking', description: 'Pakistan\'s tech workforce is fluent in English. Seamless communication.' },
      { icon: '🚀', title: 'Startup Ecosystem', description: 'Lahore\'s growing startup scene produces experienced, agile engineers.' },
      { icon: '📱', title: 'Mobile-First', description: 'Deep expertise in mobile app development for emerging market conditions.' },
      { icon: '⏰', title: 'Timezone Overlap', description: 'GMT+5 provides overlap with both European and Asian business hours.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Lahore for software development?', answer: 'Lahore is Pakistan\'s IT capital with top universities (LUMS, FAST), a growing startup ecosystem, and 65%+ cost savings.' },
      { question: 'Do your engineers speak English?', answer: 'Yes. Pakistan\'s tech professionals are fluent English speakers. All communication is in English.' },
      { question: 'What about data security?', answer: 'ISO 27001 practices, encrypted communications, NDA for all engineers, GDPR compliance.' },
      { question: 'Cost comparison?', answer: 'Senior developers in Western markets: $80-150/hr. Our Lahore-competitive rate: $20-40/hr.' },
      { question: 'Can you support fintech?', answer: 'Yes. Pakistan\'s fintech boom means our engineers understand mobile payments, digital banking, and micro-lending.' },
      { question: 'Minimum engagement?', answer: 'Start with 2 developers. Scale to 30+. Flexible monthly contracts.' },
    ],
    localRate: '$40-80',
    ourRate: '$20-40',
    savings: '50%+',
  },

  jeddah: {
    slug: 'software-development-agency-jeddah',
    cityName: 'Jeddah',
    countryName: 'Saudi Arabia',
    flag: '🇸🇦',
    timezone: 'AST (GMT+3)',
    currency: 'SAR/USD',
    title: {
      en: 'Software Development Agency Jeddah | Call IT Dev',
      fr: 'Agence de Développement Informatique Djeddah | Call IT Dev',
      ar: 'وكالة تطوير برمجيات جدة | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Jeddah, Saudi Arabia. NEOM, Vision 2030, e-commerce, fintech. 200+ engineers.',
      fr: 'Agence de développement informatique pour Djeddah. NEOM, Vision 2030, e-commerce, fintech.',
      ar: 'وكالة تطوير برمجيات جدة، السعودية. نيوم، رؤية 2030، تجارة إلكترونية، تكنولوجيا مالية.',
    },
    keywords: ['software development agency Jeddah', 'IT outsourcing Saudi Arabia', 'Vision 2030 tech Jeddah', 'app development Jeddah'],
    heroTitle: { en: 'Software Development Agency for Jeddah', ar: 'وكالة تطوير برمجيات لجدة' },
    heroSubtitle: {
      en: 'Saudi Arabia\'s commercial capital. Vision 2030, e-commerce, and digital transformation at competitive rates.',
      ar: 'العاصمة التجارية للسعودية. رؤية 2030، تجارة إلكترونية، تحول رقمي بأسعار تنافسية.',
    },
    whyTitle: { en: 'Why Jeddah Companies Choose Call IT Dev', ar: 'لماذا تختار شركات جدة Call IT Dev' },
    whyReasons: [
      { icon: '🏗️', title: 'Vision 2030', description: 'Digital transformation projects aligned with Saudi Arabia\'s Vision 2030 goals.' },
      { icon: '🕌', title: 'Arabic-Speaking Teams', description: 'Native Arabic-speaking engineers for seamless Jeddah collaboration.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Premium engineering at 55% less than Jeddah local rates.' },
      { icon: '🛒', title: 'E-commerce Boom', description: 'Saudi Arabia\'s e-commerce market is exploding. We build the platforms.' },
      { icon: '💳', title: 'FinTech & mada', description: 'Payment platforms, mada integration, SAMA compliance for Saudi fintech.' },
      { icon: '⏰', title: '2h Time Difference', description: 'Morocco GMT+1, Saudi GMT+3. Just 2 hours apart for real-time collaboration.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you support Vision 2030 projects?', answer: 'Yes. Smart city platforms, NEOM-aligned solutions, tourism tech, and government digitization.' },
      { question: 'Do your engineers speak Arabic?', answer: 'Yes. Native Arabic-speaking engineers and project managers for Jeddah clients.' },
      { question: 'SAMA compliance?', answer: 'Yes. We build SAMA-compliant fintech solutions with mada payment integration.' },
      { question: 'E-commerce for Saudi market?', answer: 'Yes. Arabic-first e-commerce platforms with SADAD, mada, and STC Pay integration.' },
      { question: 'Cost comparison?', answer: 'Jeddah senior dev: SAR 300-600/hr. Our rate: SAR 135-270/hr. 55% savings.' },
      { question: 'How fast to start?', answer: 'Team ready in 2 weeks. Arabic-speaking PM assigned from day 1.' },
    ],
    localRate: 'SAR 300-600',
    ourRate: 'SAR 135-270',
    savings: '55%+',
  },

  durban: {
    slug: 'software-development-agency-durban',
    cityName: 'Durban',
    countryName: 'South Africa',
    flag: '🇿🇦',
    timezone: 'SAST (GMT+2)',
    currency: 'ZAR/USD',
    title: {
      en: 'Software Development Agency Durban | Call IT Dev',
      fr: 'Agence de Développement Informatique Durban | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Durban, South Africa. Maritime tech, logistics, tourism platforms. 200+ engineers.',
      fr: 'Agence de développement informatique pour Durban. Tech maritime, logistique, tourisme.',
    },
    keywords: ['software development agency Durban', 'IT outsourcing Durban', 'maritime software South Africa', 'logistics development Durban'],
    heroTitle: { en: 'Software Development Agency for Durban' },
    heroSubtitle: { en: 'South Africa\'s port city goes digital. Maritime, logistics, and tourism tech at competitive rates.' },
    whyTitle: { en: 'Why Durban Companies Choose Call IT Dev' },
    whyReasons: [
      { icon: '🚢', title: 'Maritime & Port Tech', description: 'Software for Africa\'s busiest port. Cargo tracking, port management, maritime logistics.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Premium engineering at 55% less than Durban tech agency rates.' },
      { icon: '🌊', title: 'Tourism Tech', description: 'Booking platforms, tourism management, and hospitality software for KZN.' },
      { icon: '🌐', title: 'English-Speaking Teams', description: 'Fluent English communication. Same work culture.' },
      { icon: '⏰', title: '1h Time Difference', description: 'Morocco GMT+1, South Africa GMT+2. Perfect overlap.' },
      { icon: '📦', title: 'Supply Chain', description: 'Logistics, warehousing, and supply chain software for East African trade routes.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you build maritime software?', answer: 'Yes. Port management, vessel tracking, cargo documentation, and maritime logistics platforms.' },
      { question: 'Tourism tech?', answer: 'Booking engines, tour operator platforms, and hospitality management for KwaZulu-Natal\'s tourism industry.' },
      { question: 'Cost comparison?', answer: 'Durban senior dev: R600-1200/hr. Our rate: R270-540/hr. 55% savings.' },
      { question: 'Timezone?', answer: 'Just 1 hour difference (GMT+1 vs GMT+2). Full daily overlap.' },
      { question: 'Do you comply with POPIA?', answer: 'Yes. South Africa\'s POPIA and international GDPR compliance.' },
      { question: 'Minimum team?', answer: 'Start with 2 developers. Scale to 20+. Flexible engagement.' },
    ],
    localRate: 'R600-1200',
    ourRate: 'R270-540',
    savings: '55%+',
  },

  auckland: {
    slug: 'software-development-agency-auckland',
    cityName: 'Auckland',
    countryName: 'New Zealand',
    flag: '🇳🇿',
    timezone: 'NZST (GMT+12)',
    currency: 'NZD/USD',
    title: {
      en: 'Software Development Agency Auckland | Call IT Dev',
      fr: 'Agence de Développement Informatique Auckland | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Auckland, New Zealand. AgriTech, SaaS, fintech. 200+ engineers, 60% savings vs NZ rates.',
      fr: 'Agence de développement informatique pour Auckland. AgriTech, SaaS, fintech. 60% d\'économies.',
    },
    keywords: ['software development agency Auckland', 'IT outsourcing New Zealand', 'agritech development Auckland', 'SaaS development NZ'],
    heroTitle: { en: 'Software Development Agency for Auckland' },
    heroSubtitle: { en: 'New Zealand\'s tech capital. AgriTech, SaaS, and fintech at 60% savings vs Auckland rates.' },
    whyTitle: { en: 'Why Auckland Companies Choose Call IT Dev' },
    whyReasons: [
      { icon: '🌱', title: 'AgriTech Expertise', description: 'Software for NZ\'s agriculture and dairy industry. Precision farming, supply chain.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Senior devs at NZ$45-75/hr vs NZ$120-200/hr in Auckland. Same quality.' },
      { icon: '🌐', title: 'English-Speaking', description: 'Fluent English teams. Same agile methodology and work culture.' },
      { icon: '🏔️', title: 'Tourism & Outdoor Tech', description: 'Booking, adventure tourism, and sustainability platforms.' },
      { icon: '🔒', title: 'Privacy Act Compliant', description: 'NZ Privacy Act 2020 and international GDPR compliance.' },
      { icon: '📈', title: 'Startup-Friendly', description: 'Flexible MVP-to-scale engagements for Auckland\'s startup ecosystem.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you build AgriTech software?', answer: 'Yes. Precision agriculture, dairy management, livestock tracking, and agricultural supply chain platforms.' },
      { question: 'How does the timezone work?', answer: 'NZ is GMT+12, Morocco GMT+1. We have morning overlap and async workflows. Many NZ companies use this model successfully.' },
      { question: 'Cost comparison?', answer: 'Auckland senior dev: NZ$120-200/hr. Our rate: NZ$45-75/hr. 60% savings.' },
      { question: 'NZ Privacy Act compliance?', answer: 'Yes. Full compliance with NZ Privacy Act 2020 and GDPR.' },
      { question: 'Can you support Xero/MYOB integrations?', answer: 'Yes. We build integrations with NZ\'s key business platforms.' },
      { question: 'Minimum engagement?', answer: 'Start with 2 developers. Scale as needed. Flexible contracts.' },
    ],
    localRate: 'NZ$120-200',
    ourRate: 'NZ$45-75',
    savings: '60%+',
  },

  vancouver: {
    slug: 'software-development-agency-vancouver',
    cityName: 'Vancouver',
    countryName: 'Canada',
    flag: '🇨🇦',
    timezone: 'PST (GMT-8)',
    currency: 'CAD/USD',
    title: {
      en: 'Software Development Agency Vancouver | Call IT Dev',
      fr: 'Agence de Développement Informatique Vancouver | Call IT Dev',
      zh: '温哥华软件开发公司 | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Vancouver, Canada. Gaming, VFX, SaaS, AI. 200+ engineers, 60% savings vs Vancouver rates.',
      fr: 'Agence de développement informatique pour Vancouver. Gaming, VFX, SaaS, IA. 60% d\'économies.',
      zh: '为温哥华提供软件开发服务。游戏、VFX、SaaS、AI。比温哥华价格节省60%。',
    },
    keywords: ['software development agency Vancouver', 'IT outsourcing Vancouver', 'gaming development Vancouver', 'VFX software Vancouver'],
    heroTitle: { en: 'Software Development Agency for Vancouver', fr: 'Agence de Développement pour Vancouver' },
    heroSubtitle: { en: 'Canada\'s Pacific tech hub. Gaming, VFX, and SaaS development at 60% savings vs Vancouver rates.' },
    whyTitle: { en: 'Why Vancouver Companies Choose Call IT Dev' },
    whyReasons: [
      { icon: '🎮', title: 'Gaming & VFX', description: 'Vancouver is a global gaming/VFX hub. We build game backends, tools, and pipeline software.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Senior devs at CAD$45-75/hr vs CAD$120-180/hr in Vancouver.' },
      { icon: '🌐', title: 'Bilingual Teams', description: 'English and French-speaking engineers for Canadian bilingual requirements.' },
      { icon: '☁️', title: 'Cloud-Native', description: 'AWS, Azure, GCP expertise for Vancouver\'s cloud-first companies.' },
      { icon: '🤖', title: 'AI & ML', description: 'Vancouver\'s AI corridor expertise — deep learning, NLP, computer vision.' },
      { icon: '🌲', title: 'CleanTech', description: 'Sustainability software, carbon tracking, and green energy platforms.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you build gaming/VFX software?', answer: 'Yes. Game backends, rendering pipelines, asset management, and production tools for Vancouver\'s gaming/VFX studios.' },
      { question: 'How does the timezone work?', answer: 'Vancouver is GMT-8, Morocco GMT+1. We have morning overlap (Vancouver AM = Morocco afternoon). Async + overlap model.' },
      { question: 'Do you support Canadian bilingual requirements?', answer: 'Yes. Our teams include French and English-speaking engineers for bilingual projects.' },
      { question: 'Cost comparison?', answer: 'Vancouver senior dev: CAD$120-180/hr. Our rate: CAD$45-75/hr. 60% savings.' },
      { question: 'PIPEDA compliance?', answer: 'Yes. Canadian PIPEDA and GDPR compliant. Enterprise security standards.' },
      { question: 'How fast to start?', answer: 'Team ready in 2 weeks. First sprint week 3.' },
    ],
    localRate: 'CAD$120-180',
    ourRate: 'CAD$45-75',
    savings: '60%+',
  },

  chicago: {
    slug: 'software-development-agency-chicago',
    cityName: 'Chicago',
    countryName: 'United States',
    flag: '🇺🇸',
    timezone: 'CST (GMT-6)',
    currency: 'USD',
    title: {
      en: 'Software Development Agency Chicago | Call IT Dev',
      fr: 'Agence de Développement Informatique Chicago | Call IT Dev',
      es: 'Agencia Desarrollo Software Chicago | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Chicago, Illinois. FinTech, logistics, SaaS, AI. 200+ engineers, 65% savings vs Chicago rates.',
      fr: 'Agence de développement informatique pour Chicago. FinTech, logistique, SaaS. 65% d\'économies.',
      es: 'Agencia de desarrollo de software para Chicago. FinTech, logística, SaaS, IA. 65% ahorro.',
    },
    keywords: ['software development agency Chicago', 'IT outsourcing Chicago', 'fintech development Chicago', 'logistics software Chicago'],
    heroTitle: { en: 'Software Development Agency for Chicago' },
    heroSubtitle: { en: 'The Windy City\'s enterprise tech hub. FinTech, logistics, and SaaS at 65% savings vs Chicago rates.' },
    whyTitle: { en: 'Why Chicago Companies Choose Call IT Dev' },
    whyReasons: [
      { icon: '🏦', title: 'FinTech & Trading', description: 'Chicago is America\'s financial hub. Trading platforms, derivatives tech, and compliance.' },
      { icon: '💰', title: '65% Cost Savings', description: 'Senior devs at $35-55/hr vs $100-175/hr in Chicago.' },
      { icon: '📦', title: 'Logistics Capital', description: 'Supply chain, freight tech, and logistics software for America\'s transport hub.' },
      { icon: '🌐', title: 'English-Speaking Teams', description: 'Fluent English. Same agile methodology and enterprise standards.' },
      { icon: '⏰', title: 'Timezone Overlap', description: '7 hours of daily overlap with CST. Morning standups work perfectly.' },
      { icon: '🏢', title: 'Enterprise Grade', description: 'SOC 2, HIPAA, PCI-DSS compliance for Chicago\'s regulated industries.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you build trading/fintech software?', answer: 'Yes. Trading platforms, risk management, derivatives pricing, and compliance tools for Chicago\'s financial sector.' },
      { question: 'Logistics software?', answer: 'TMS, WMS, freight brokerage platforms, and supply chain visibility for Chicago\'s logistics industry.' },
      { question: 'How does the timezone work?', answer: 'Morocco GMT+1, Chicago GMT-6. We have 7 hours of overlap. Morning standups at 9am CST = 4pm Morocco.' },
      { question: 'Cost comparison?', answer: 'Chicago senior dev: $100-175/hr (agency). Our rate: $35-55/hr. 65% savings.' },
      { question: 'SOC 2 compliance?', answer: 'Yes. SOC 2 Type II aligned, HIPAA compliant, PCI-DSS for financial applications.' },
      { question: 'How fast to start?', answer: 'Team ready in 2 weeks. Technical interviews within 5 business days.' },
    ],
    localRate: '$100-175',
    ourRate: '$35-55',
    savings: '65%+',
  },
};
